import { Box, Group, Text, UnstyledButton, Collapse } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type { ReactNode } from 'react';

interface SectionToggleRowProps {
  title: ReactNode;
  meta?: ReactNode;
  rightSection?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children?: ReactNode;
  /**
   * `plain` (Standard): flache Zeile, durch eine obere Trennlinie abgesetzt -- fuer die uebrigen
   * Verwender.
   * `card`: eigenstaendiger Kartencontainer (D-12, siehe `design/img_4.png`). Die Drehoefen-Ebene
   * nutzt ihn grau (Standard `cardBg`), die Messungen darin weiss (`cardBg` = white) -- ansonsten
   * sind Ofen und Messung strukturell identisch aufgebaut.
   */
  variant?: 'plain' | 'card';
  /** Hintergrundfarbe der Karte (nur `variant="card"`). Standard: grau (`vdzGray.1`). */
  cardBg?: string;
}

export function SectionToggleRow({
  title,
  meta,
  rightSection,
  expanded,
  onToggle,
  children,
  variant = 'plain',
  cardBg,
}: SectionToggleRowProps) {
  const ChevronIcon = expanded ? IconChevronDown : IconChevronRight;
  const isCard = variant === 'card';

  return (
    <Box
      style={
        isCard
          ? {
              backgroundColor: cardBg ?? 'var(--mantine-color-vdzGray-1)',
              borderRadius: 'var(--mantine-radius-md)',
              overflow: 'hidden',
            }
          : { borderTop: '1px solid var(--mantine-color-vdzGray-2)' }
      }
    >
      {/*
        Der Innenabstand sitzt bewusst NICHT hier, sondern in den Kindern: liegt er auf der
        Gruppe, endet die Schaltflaeche an der Textkante und der Streifen darueber/darunter
        ist tot. Mit dem Padding im Button reicht die Klickflaeche ueber die volle Kopfhoehe.
      */}
      <Group justify="space-between" wrap="nowrap" gap="sm">
        {/* Titel links -- grosse Klickflaeche zum Auf-/Zuklappen. */}
        <UnstyledButton
          onClick={onToggle}
          style={{
            flex: 1,
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            textAlign: 'left',
            minWidth: 0,
            padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
          }}
        >
          {/* component="div" statt des Mantine-Standards <p>: manche Verwender (FormularDSectionI,
              D-14) geben hier einen title mit verschachtelten Block-Elementen (<Group>, rendert
              <div>) mit -- ein <div> in einem <p> ist ungueltiges HTML und loeste einen
              Hydration-Fehler in der Konsole aus (Plan 23-03 Task-3-Nachbesserung). <div>
              truncatet identisch zu <p>, nur das Tag ist ein anderes. */}
          <Text component="div" fw={600} truncate>
            {title}
          </Text>
        </UnstyledButton>
        {/* Rechts, von links nach rechts: meta (z. B. "21 Messungen") -> Aktion (Loeschen) ->
            Chevron ganz aussen. */}
        <Group gap="sm" wrap="nowrap" pr="md" py="sm">
          {meta && (
            <Text size="sm" fw={700} c="vdzGray.7">
              {meta}
            </Text>
          )}
          {rightSection}
          <UnstyledButton
            onClick={onToggle}
            aria-label={expanded ? 'Zuklappen' : 'Aufklappen'}
            style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}
          >
            <ChevronIcon size={16} stroke={1.5} color="var(--mantine-color-vdzGray-7)" />
          </UnstyledButton>
        </Group>
      </Group>
      <Collapse expanded={expanded}>
        <Box px="md" pb="md">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
